/**
 * DataTableV2 — the shared v2 data-table pattern (Phase 1 fan-out foundation).
 *
 * The recurring table structure the canonical spec repeats on BOTH the All
 * Candidates table view and the Actions Log (design/v2-direction/index.html
 * `.table-wrap` + `table.tbl`, lines 412-424, 820-836, 1228-1233):
 *   • a bordered, rounded, shadow-sm "table-wrap" frame with horizontal scroll,
 *   • a muted-tinted sticky-capable header row (12px, 500, muted),
 *   • hairline-separated 13.5px body rows with an optional clickable hover,
 *   • faces where the spec shows them (avatar-forward rows — pass a
 *     <CandidateAvatar/> in the row's first cell; this pattern does not force
 *     facelessness, per README §3).
 *
 * This wraps the vendored shadcn Table primitives so a batch declares columns +
 * rows and gets the exact v2 density/hover/chrome for free.
 *
 * HOW A BATCH USES THIS
 *   const cols = [{ key: "name", header: "Candidate" }, …];
 *   <DataTableV2 columns={cols} rows={data} getRowKey={r => r.id}
 *     onRowClick={r => nav(r.id)} renderCell={(r, c) => …} />
 *   All Candidates → lead the name cell with <CandidateAvatar/> + rank badge.
 *   Actions Log    → plain text cells (time / actor / action / target).
 *
 * Density: header px-4 py-2.5, cell px-4 py-[11px] — under the §2.0 ceiling.
 * Token discipline: all chrome via the tokenized Table primitives + semantic
 * utilities; no hex / rgb / hsl(var()). Guard-clean; renders in light + dark.
 */
import type { ReactNode } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

export interface DataTableColumn {
  /** Stable column id (used as React key + passed to renderCell). */
  key: string;
  /** Header cell content. Empty string renders a blank header (e.g. actions). */
  header: ReactNode;
  /** Optional fixed width, e.g. "170px" / "44px" (matches spec column widths). */
  width?: string;
  /** Extra classes on the header <th> (e.g. text-right for numeric columns). */
  headClassName?: string;
  /** Extra classes on every body <td> in this column. */
  cellClassName?: string;
  /**
   * Sort axis this header controls. When set AND the table is given `onSort`,
   * the header renders as a sort toggle (arrow indicator + click). Optional —
   * columns without a `sortField` render as plain, non-interactive headers.
   */
  sortField?: string;
}

export interface DataTableV2Props<Row> {
  columns: DataTableColumn[];
  rows: Row[];
  /** Stable per-row key. */
  getRowKey: (row: Row, index: number) => string | number;
  /** Cell renderer — return the content for (row, column). */
  renderCell: (row: Row, column: DataTableColumn, index: number) => ReactNode;
  /** Optional row click → clickable cursor + hover highlight (spec `.clickable`). */
  onRowClick?: (row: Row, index: number) => void;
  /** Optional per-row extra classes (e.g. `opacity-60` for opted-out rows). */
  getRowClassName?: (row: Row, index: number) => string | undefined;
  /** Shown when rows is empty. */
  emptyState?: ReactNode;
  /** Sticky header (long scroll surfaces). Default false. */
  stickyHeader?: boolean;
  /** Active sort axis (matches a column's `sortField`). Enables sort chrome. */
  sortField?: string;
  /** Active sort direction. */
  sortDir?: "asc" | "desc";
  /** Sort toggle handler — a sortable header calls this with its `sortField`. */
  onSort?: (field: string) => void;
  className?: string;
}

export function DataTableV2<Row>({
  columns,
  rows,
  getRowKey,
  renderCell,
  onRowClick,
  getRowClassName,
  emptyState,
  stickyHeader = false,
  sortField,
  sortDir,
  onSort,
  className,
}: DataTableV2Props<Row>): JSX.Element {
  return (
    // Canonical .table-wrap: bordered rounded shadow-sm frame, own bg, x-scroll.
    <div
      className={cn(
        "overflow-x-auto rounded-[var(--radius,0.5rem)] border border-border-default bg-surface-card shadow-sm",
        className,
      )}
    >
      <Table>
        <TableHeader>
          <TableRow className="border-border-default">
            {columns.map((col) => {
              const isSortable = !!col.sortField && !!onSort;
              const isActive = isSortable && col.sortField === sortField;
              return (
                <TableHead
                  key={col.key}
                  style={col.width ? { width: col.width } : undefined}
                  aria-sort={
                    isActive
                      ? sortDir === "desc"
                        ? "descending"
                        : "ascending"
                      : undefined
                  }
                  className={cn(
                    stickyHeader && "sticky top-0 z-10 backdrop-blur",
                    col.headClassName,
                  )}
                >
                  {isSortable ? (
                    <button
                      type="button"
                      onClick={() => onSort!(col.sortField!)}
                      className={cn(
                        "inline-flex items-center gap-1 select-none transition-colors",
                        "hover:text-text-primary focus-visible:outline-none",
                        "focus-visible:ring-2 focus-visible:ring-border-focus rounded-sm",
                        isActive ? "text-text-primary" : "text-inherit",
                      )}
                    >
                      {col.header}
                      <span
                        className="text-[10px] leading-none"
                        aria-hidden
                        style={{ opacity: isActive ? 1 : 0.35 }}
                      >
                        {isActive ? (sortDir === "desc" ? "▼" : "▲") : "↕"}
                      </span>
                    </button>
                  ) : (
                    col.header
                  )}
                </TableHead>
              );
            })}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 && emptyState ? (
            <TableRow className="border-0 hover:bg-transparent">
              <TableCell
                colSpan={columns.length}
                className="py-6 text-center text-text-secondary"
              >
                {emptyState}
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row, i) => (
              <TableRow
                key={getRowKey(row, i)}
                onClick={onRowClick ? () => onRowClick(row, i) : undefined}
                className={cn(
                  "border-border-default",
                  onRowClick &&
                    "cursor-pointer transition-colors hover:bg-surface-subtle/60",
                  getRowClassName?.(row, i),
                )}
              >
                {columns.map((col) => (
                  <TableCell
                    key={col.key}
                    className={cn("text-text-primary", col.cellClassName)}
                  >
                    {renderCell(row, col, i)}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
